import styled, { css } from 'styled-components';
import { header3 } from '@ardoq/typography';
import { colors, s16, s8, shadowS } from '@ardoq/design-tokens';
import { DragButton } from '../atoms/DragButton';
import { EditModeOptionsButton } from '../atoms/EditModeOptionsButton';
import { BUTTON_SELECTOR, GhostButton } from '@ardoq/button';
import { TextOverflow } from '@ardoq/popovers';
import {
  DASHBOARD_MIN_SCREEN_WIDTH_SMALL,
  HEADER_EDIT_BUTTON_ID,
  HEADER_WIDGET_COL_SPAN,
  ROW_HEIGHT,
} from '../../consts';
import { HeaderBarButtonGroup } from '../atoms/HeaderBarButtonGroup';
import { EditIcon } from '@ardoq/icons';
import { Layout } from 'react-grid-layout';
import { HeaderWidgetProps } from '../renderTypes';
import { type WithI18nLocale, t } from '@ardoq/i18n';

const HeaderText = styled(TextOverflow)`
  ${header3};
  color: ${colors.textDefault};
`;
const Placeholder = styled.h3`
  ${header3};
  color: ${colors.textSubtle};
`;

const HeaderBarContainer = styled.div<{
  $isSelected: boolean;
  $layout: Layout;
  $hasErrorOutline: boolean;
}>`
  background-color: ${colors.surfaceDefault};
  box-shadow: ${shadowS};
  width: 100%;
  border-radius: ${s8};
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${ROW_HEIGHT}px;
  grid-column: auto / span 1;
  grid-row: auto / span ${({ $layout: { h } }) => h};

  @media (min-width: ${DASHBOARD_MIN_SCREEN_WIDTH_SMALL}px) {
    grid-column: 1 / span ${HEADER_WIDGET_COL_SPAN};
    grid-row: ${({ $layout: { y } }) => y + 1} / span
      ${({ $layout: { h } }) => h};
  }

  ${BUTTON_SELECTOR} {
    display: none;
  }

  ${HeaderText}, ${Placeholder} {
    margin: 0 ${s16};
  }

  &:hover {
    outline: 2px solid ${colors.borderActionHover};

    ${BUTTON_SELECTOR} {
      display: block;
    }
  }

  ${({ $isSelected, $hasErrorOutline }) =>
    $isSelected
      ? css`
          outline: 2px solid ${colors.borderActionHover};
        `
      : $hasErrorOutline &&
        css`
          outline: 1px solid ${colors.borderErrorStrong};
        `}
`;

export const HeaderWidget = (props: HeaderWidgetProps & WithI18nLocale) => {
  const { content, layout, isInEditMode, i18nLocale } = props;

  return (
    <HeaderBarContainer
      $isSelected={props.isInEditMode && props.isSelected}
      $hasErrorOutline={props.isInEditMode && props.hasError}
      $layout={layout}
    >
      {isInEditMode && !props.isInLinkDashboardFiltersMode && (
        <DragButton isSaving={props.isSaving} i18nLocale={i18nLocale} />
      )}
      {content ? (
        <HeaderText>{content}</HeaderText>
      ) : (
        <Placeholder>
          {t(
            i18nLocale,
            'dashboard:headerWidget.headingNamePlaceholder',
            'Heading name'
          )}
        </Placeholder>
      )}
      {isInEditMode && !props.isInLinkDashboardFiltersMode && (
        <HeaderBarButtonGroup>
          <GhostButton
            dataTestId={HEADER_EDIT_BUTTON_ID}
            data-tooltip-text={t(
              i18nLocale,
              'dashboard:headerWidget.editHeading',
              'Edit heading'
            )}
            onClick={props.commands.selectWidget}
          >
            <EditIcon />
          </GhostButton>
          <EditModeOptionsButton
            canEdit={true}
            copyWidget={props.commands.copyWidget}
            deleteWidget={props.commands.deleteWidget}
            i18nLocale={i18nLocale}
          />
        </HeaderBarButtonGroup>
      )}
    </HeaderBarContainer>
  );
};
